// Prevent save if phone is missing or invalid

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Col, Row } from 'reactstrap';
import UploadLetter from './UploadLetter';

import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

function isValidUrl(url) {
	// Accepts www.example.com, http(s)://www.example.com, etc.
	return /^(https?:\/\/)?(www\.)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/.test(url);
}

const Recommendations = forwardRef(({ applicationId, onSave, onNext, data, onDataChange, resumeData }, ref) => {
	const [errors, setErrors] = useState({});
	const [showValidation, setShowValidation] = useState(false);

	// Validation function
	const validate = () => {
		const newErrors = {};
		// Recommendation 1
		if (!recommendation1.name?.trim()) newErrors.name1 = 'Name is required';
		if (!recommendation1.email?.trim()) newErrors.email1 = 'Email is required';
		if (!recommendation1.mobile?.trim()) newErrors.mobile1 = 'Mobile number is required';
		if (!recommendation1.designation?.trim()) newErrors.designation1 = 'Designation is required';
		if (!recommendation1.institution?.trim()) newErrors.institution1 = 'Institution is required';
		if (!recommendation1.institution_webpage?.trim())
			newErrors.institution_webpage1 = 'Institution webpage is required';
		if (!recommendation1.file) newErrors.file1 = 'Recommendation letter is required';

		// Additional validations for recommendation 1
		if (recommendation1.email?.trim() && !/\S+@\S+\.\S+/.test(recommendation1.email)) {
			newErrors.email1 = 'Please enter a valid email address';
		}
		if (recommendation1.institution_webpage?.trim() && !isValidUrl(recommendation1.institution_webpage)) {
			newErrors.institution_webpage1 = 'Please enter a valid webpage in format www.example.com';
		}
		if (webpageError1) newErrors.institution_webpage1 = webpageError1;
		if (phoneError1) newErrors.mobile1 = phoneError1;

		// Recommendation 2
		if (!recommendation2.name?.trim()) newErrors.name2 = 'Name is required';
		if (!recommendation2.email?.trim()) newErrors.email2 = 'Email is required';
		if (!recommendation2.mobile?.trim()) newErrors.mobile2 = 'Mobile number is required';
		if (!recommendation2.designation?.trim()) newErrors.designation2 = 'Designation is required';
		if (!recommendation2.institution?.trim()) newErrors.institution2 = 'Institution is required';
		if (!recommendation2.institution_webpage?.trim())
			newErrors.institution_webpage2 = 'Institution webpage is required';
		if (!recommendation2.file) newErrors.file2 = 'Recommendation letter is required';

		// Additional validations for recommendation 2
		if (recommendation2.email?.trim() && !/\S+@\S+\.\S+/.test(recommendation2.email)) {
			newErrors.email2 = 'Please enter a valid email address';
		}
		if (recommendation2.institution_webpage?.trim() && !isValidUrl(recommendation2.institution_webpage)) {
			newErrors.institution_webpage2 = 'Please enter a valid webpage in format www.example.com';
		}
		if (webpageError2) newErrors.institution_webpage2 = webpageError2;
		if (phoneError2) newErrors.mobile2 = phoneError2;

		setErrors(newErrors);
		const isValid = Object.keys(newErrors).length === 0;
		if (!isValid) {
			setShowValidation(true);
		}
		return isValid;
	};

	// Expose validate method to parent Form.jsx
	useImperativeHandle(ref, () => ({
		validate: () => {
			const isValid = validate();
			if (!isValid) {
				window.scrollTo(0, 0); // Scroll to top to show errors
				setShowValidation(true); // Force show validation messages
			}
			return isValid;
		},
		getFormData: () => {
			validate(); // Always validate when getting form data
			return { recommendation1, recommendation2 };
		},
	}));

	// console.log("Received resumeData in Recommendations:", data);
	const [recommendation1, setRecommendation1] = useState({
		name: '',
		email: '',
		mobile: '',
		designation: '',
		institution: '',
		institution_webpage: '',
		file: null,
	});

	const [recommendation2, setRecommendation2] = useState({
		name: '',
		email: '',
		mobile: '',
		designation: '',
		institution: '',
		institution_webpage: '',
		file: null,
	});

	// Load saved data when component mounts or data changes
	useEffect(() => {
		console.log("🔍 Recommendations component received data:", data);
		if (data) {
			// Process recommendation1
			if (data.recommendation1) {
				// console.log("🔍 Processing recommendation1:", data.recommendation1);
				const rec1 = { ...data.recommendation1 };

				
				// Handle file loading for recommendation1 - check both file and letter_url
				const fileUrl = rec1.file || rec1.letter_url;
				if (fileUrl && typeof fileUrl === 'string') {
					// console.log("📁 Loading recommendation1 file from URL:", fileUrl);
					fetch(fileUrl)
						.then((res) => {
							if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
							return res.blob();
						})
						.then((blob) => {
							const file = new File([blob], 'recommendation1.pdf', { type: 'application/pdf' });
							setRecommendation1((prev) => ({
								...prev,
								...rec1,
								file,
							}));
							// console.log("✅ Recommendation1 file loaded successfully");
						})
						.catch((err) => {
							// Still set the data even if file loading fails
							setRecommendation1((prev) => ({
								...prev,
								...rec1,
							}));
						});
				} else {
					setRecommendation1((prev) => ({
						...prev,
						...rec1,
					}));
					 console.log("✅ Recommendation1 data set (no file to load) - fileUrl:", fileUrl, );
				}
			} else {
				console.log('⚠️ No recommendation1 data found');
			}

			// Process recommendation2
			if (data.recommendation2) {
				// console.log("🔍 Processing recommendation2:", data.recommendation2);
				const rec2 = { ...data.recommendation2 };

				// Debug file fields
				// console.log("🔍 Recommendation2 file fields:", {
				//     file: rec2.file,
				//     letter_url: rec2.letter_url,
				//     fileType: typeof rec2.file,
				//     letterUrlType: typeof rec2.letter_url
				// });

				// Handle file loading for recommendation2 - check both file and letter_url
				const fileUrl = rec2.file || rec2.letter_url;
				if (fileUrl && typeof fileUrl === 'string') {
					// console.log("📁 Loading recommendation2 file from URL:", fileUrl);
					fetch(fileUrl)
						.then((res) => {
							if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
							return res.blob();
						})
						.then((blob) => {
							const file = new File([blob], 'recommendation2.pdf', { type: 'application/pdf' });
							setRecommendation2((prev) => ({
								...prev,
								...rec2,
								file,
							}));
							// console.log("✅ Recommendation2 file loaded successfully");
						})
						.catch((err) => {
							// console.error('❌ Error loading recommendation2 file:', err);
							// Still set the data even if file loading fails
							setRecommendation2((prev) => ({
								...prev,
								...rec2,
							}));
						});
				} else {
					setRecommendation2((prev) => ({
						...prev,
						...rec2,
					}));
					// console.log("✅ Recommendation2 data set (no file to load) - fileUrl:", fileUrl);
				}
			} else {
				console.log('⚠️ No recommendation2 data found');
			}
		} else {
			console.log('⚠️ No data received in Recommendations component');
		}
	}, [data]);

	// useEffect(() => {
	// 	if (resumeData) {
	// 		setRecommendation1((prev) => ({
	// 			...prev,
	// 			name: resumeData.referees.name || '',
	// 			email: resumeData.referees.email || '',
	// 			mobile: resumeData.referees.mobile || '',
	// 			designation: resumeData.referees.designation || '',
	// 			institution: resumeData.referees.institution || '',
	// 			institution_webpage: resumeData.referees.institutionWebpage || '',
	// 		}));
	// 	}
	// }, [resumeData]);

	const [webpageError1, setWebpageError1] = useState('');
	const [webpageError2, setWebpageError2] = useState('');

	const [phoneError1, setPhoneError1] = useState('');
	const [phoneError2, setPhoneError2] = useState('');

	const handleChange = (recommendationNumber, e) => {
		const { name, value } = e.target;
		const sanitizedValue = value === null ? '' : value;
		if (name === 'institution_webpage') {
			if (!isValidUrl(sanitizedValue)) {
				if (recommendationNumber === 1) setWebpageError1('Please enter a valid URL (e.g. https://example.com)');
				else setWebpageError2('Please enter a valid URL (e.g. https://example.com)');
			} else {
				if (recommendationNumber === 1) setWebpageError1('');
				else setWebpageError2('');
			}
		}
		if (name === 'mobile') {
			// Clear phone error if user is typing in the input field (not PhoneInput)
			if (recommendationNumber === 1) setPhoneError1('');
			else setPhoneError2('');
		}
		if (recommendationNumber === 1) {
			const updatedRecommendation1 = {
				...recommendation1,
				[name]: sanitizedValue,
			};
			setRecommendation1(updatedRecommendation1);
			onDataChange({
				recommendation1: updatedRecommendation1,
				recommendation2,
			});
		} else {
			const updatedRecommendation2 = {
				...recommendation2,
				[name]: sanitizedValue,
			};
			setRecommendation2(updatedRecommendation2);
			onDataChange({
				recommendation1,
				recommendation2: updatedRecommendation2,
			});
		}
	};

	// Handler for PhoneInput (react-phone-input-2)
	const handlePhoneChange = (recommendationNumber, value, country, ...rest) => {
		// Validate phone number using libphonenumber-js
		let isValid = false;
		let errorMsg = '';
		if (value) {
			try {
				const phoneNumber = parsePhoneNumberFromString('+' + value);
				isValid = phoneNumber && phoneNumber.isValid();
			} catch (err) {
				isValid = false;
			}
		}
		if (!isValid) {
			errorMsg = 'Please enter a valid phone number.';
		}
		if (recommendationNumber === 1) {
			setRecommendation1((prev) => ({ ...prev, mobile: value }));
			setPhoneError1(errorMsg);
			onDataChange({ recommendation1: { ...recommendation1, mobile: value }, recommendation2 });
		} else {
			setRecommendation2((prev) => ({ ...prev, mobile: value }));
			setPhoneError2(errorMsg);
			onDataChange({ recommendation1, recommendation2: { ...recommendation2, mobile: value } });
		}
	};

	const handleFileSelect = (recommendationNumber, file) => {
		if (recommendationNumber === 1) {
			const updatedRecommendation1 = {
				...recommendation1,
				file,
			};
			setRecommendation1(updatedRecommendation1);
			onDataChange({
				recommendation1: updatedRecommendation1,
				recommendation2,
			});
		} else {
			const updatedRecommendation2 = {
				...recommendation2,
				file,
			};
			setRecommendation2(updatedRecommendation2);
			onDataChange({
				recommendation1,
				recommendation2: updatedRecommendation2,
			});
		}
	};

	// Prevent next/save if validation fails
	// useEffect(() => {
	//     if (typeof window !== 'undefined') {
	//         window.preventStepAdvance = !validate();
	//     }
	// }, [recommendation1, recommendation2]);

	return (
		<>
			<Row>
				<Col xl="12">
					<h2 className="personalinfo_head">Referee 1</h2>
				</Col>
			</Row>

			<div className="py-4">
				<Row>
					<Col xl="4">
						<div className="personal_formdetail">
							<h5>
								Referee Name <span>*</span>
							</h5>
							<input
								type="text"
								name="name"
								value={recommendation1.name}
								onChange={(e) => handleChange(1, e)}
								className={`form-control form_custom${showValidation && errors.name1 ? ' is-invalid' : ''}`}
								placeholder="Please enter Name"
								required
							/>
							{showValidation && errors.name1 && <div className="invalid-feedback d-block">{errors.name1}</div>}
						</div>
					</Col>

					<Col xl="4">
						<div className="personal_formdetail">
							<h5>
								Referee Email address<span>*</span>
							</h5>
							<input
								type="email"
								name="email"
								value={recommendation1.email}
								onChange={(e) => handleChange(1, e)}
								className={`form-control form_cap form_custom${showValidation && errors.email1 ? ' is-invalid' : ''}`}
								placeholder="Please enter Email"
								required
							/>
							{showValidation && errors.email1 && <div className="invalid-feedback d-block">{errors.email1}</div>}
						</div>
					</Col>

					{/* <Col xl="4">
                        <div className="personal_formdetail">
                            <h5>Referee Mobile number<span>*</span></h5>
                            <div className="phone_input">
                                <PhoneInput
                                    country={"in"}
                                    value={recommendation1.mobile}
                                    onChange={(value, country, e, formattedValue) => handlePhoneChange(1, value, country)}
                                    placeholder="Please enter the Mobile number"
                                    inputProps={{ name: "mobile", required: true }}
                                />
                                {phoneError1 && (
                                    <div className="invalid-feedback" style={{ display: 'block' }}>{phoneError1}</div>
                                )}
                            </div>
                        </div>
                    </Col> */}
					<Col xl="4">
						<div className="personal_formdetail">
							<h5>
								Referee Mobile number<span>*</span>
							</h5>
							<div className="phone_input">
								<PhoneInput
									country="in"
									countryCodeEditable={false}
									value={recommendation1.mobile}
									onChange={(value, country, e, formattedValue) => handlePhoneChange(1, value, country)}
									placeholder="Please enter the Mobile number"
									inputProps={{ name: 'mobile', required: true }}
									className={showValidation && errors.mobile1 ? 'is-invalid' : ''}
								/>
								{showValidation && errors.mobile1 && <div className="invalid-feedback d-block">{errors.mobile1}</div>}
								{phoneError1 && (
									<div className="invalid-feedback" style={{ display: 'block' }}>
										{phoneError1}
									</div>
								)}
							</div>
						</div>
					</Col>

					<Col xl="12">
						<div className="personal_formdetail">
							<h5>
								Current/Last Designation<span>*</span>
							</h5>
							<input
								type="text"
								name="designation"
								value={recommendation1.designation}
								onChange={(e) => handleChange(1, e)}
								className={`form-control form_custom${showValidation && errors.designation1 ? ' is-invalid' : ''}`}
								placeholder="Description of designation"
								required
							/>
							{showValidation && errors.designation1 && (
								<div className="invalid-feedback d-block">{errors.designation1}</div>
							)}
						</div>
					</Col>

					<Col xl="6">
						<div className="personal_formdetail">
							<h5>
								Current/Last Institution<span>*</span>
							</h5>
							<input
								type="text"
								name="institution"
								value={recommendation1.institution}
								onChange={(e) => handleChange(1, e)}
								className={`form-control form_custom${showValidation && errors.institution1 ? ' is-invalid' : ''}`}
								placeholder="Institution Name"
								required
							/>
							{showValidation && errors.institution1 && (
								<div className="invalid-feedback d-block">{errors.institution1}</div>
							)}
						</div>
					</Col>

					<Col xl="6">
						<div className="personal_formdetail">
							<h5>&nbsp;</h5>
							<input
								type="text"
								name="institution_webpage"
								value={recommendation1.institution_webpage}
								onChange={(e) => handleChange(1, e)}
								className={`form-control form_cap form_custom${
									showValidation && errors.institution_webpage1 ? ' is-invalid' : ''
								}`}
								placeholder="Institution Webpage"
								required
							/>
							{showValidation && errors.institution_webpage1 && (
								<div className="invalid-feedback d-block">{errors.institution_webpage1}</div>
							)}
							{webpageError1 && (
								<div className="invalid-feedback" style={{ display: 'block' }}>
									{webpageError1}
								</div>
							)}
						</div>
					</Col>
				</Row>

				<Row>
					<Col xl="12">
						<UploadLetter onFileSelect={(file) => handleFileSelect(1, file)} initialFile={recommendation1.file} />
						{showValidation && errors.file1 && <div className="invalid-feedback d-block">{errors.file1}</div>}
					</Col>
				</Row>
			</div>

			<Row>
				<Col xl="12">
					<h2 className="personalinfo_head">Referee 2</h2>
				</Col>
			</Row>

			<div className="py-4">
				<Row>
					<Col xl="4">
						<div className="personal_formdetail">
							<h5>
								Referee Name<span>*</span>
							</h5>
							<input
								type="text"
								name="name"
								value={recommendation2.name}
								onChange={(e) => handleChange(2, e)}
								className={`form-control form_custom${showValidation && errors.name2 ? ' is-invalid' : ''}`}
								placeholder="Please enter name"
								required
							/>
							{showValidation && errors.name2 && <div className="invalid-feedback d-block">{errors.name2}</div>}
						</div>
					</Col>

					<Col xl="4">
						<div className="personal_formdetail">
							<h5>
								Referee Email address<span>*</span>
							</h5>
							<input
								type="email"
								name="email"
								value={recommendation2.email}
								onChange={(e) => handleChange(2, e)}
								className={`form-control form_cap form_custom${showValidation && errors.email2 ? ' is-invalid' : ''}`}
								placeholder="Please enter Email"
								required
							/>
							{showValidation && errors.email2 && <div className="invalid-feedback d-block">{errors.email2}</div>}
						</div>
					</Col>

					{/* <Col xl="4">
                        <div className="personal_formdetail">
                            <h5>Referee Mobile number<span>*</span></h5>
                            <input
                                type="number"
                                name="mobile"
                                value={recommendation2.mobile}
                                onChange={(e) => handleChange(2, e)}
                                className='form-control form_custom'
                                placeholder="Please enter the Mobile number"
                                required
                            />
                        </div>
                    </Col> */}

					<Col xl="4">
						<div className="personal_formdetail">
							<h5>
								Referee Mobile number<span>*</span>
							</h5>
							<div className="phone_input">
								<PhoneInput
									country="in"
									countryCodeEditable={false}
									value={recommendation2.mobile}
									onChange={(value, country, e, formattedValue) => handlePhoneChange(2, value, country)}
									placeholder="Please enter the Mobile number"
									inputProps={{ name: 'mobile', required: true }}
									className={showValidation && errors.mobile2 ? 'is-invalid' : ''}
								/>
								{showValidation && errors.mobile2 && <div className="invalid-feedback d-block">{errors.mobile2}</div>}
								{phoneError2 && (
									<div className="invalid-feedback" style={{ display: 'block' }}>
										{phoneError2}
									</div>
								)}
							</div>
						</div>
					</Col>

					<Col xl="12">
						<div className="personal_formdetail">
							<h5>
								Current/Last Designation<span>*</span>
							</h5>
							<input
								type="text"
								name="designation"
								value={recommendation2.designation}
								onChange={(e) => handleChange(2, e)}
								className={`form-control form_custom${showValidation && errors.designation2 ? ' is-invalid' : ''}`}
								placeholder="Description of designation"
								required
							/>
							{showValidation && errors.designation2 && (
								<div className="invalid-feedback d-block">{errors.designation2}</div>
							)}
						</div>
					</Col>

					<Col xl="6">
						<div className="personal_formdetail">
							<h5>
								Current/Last Institution<span>*</span>
							</h5>
							<input
								type="text"
								name="institution"
								value={recommendation2.institution}
								onChange={(e) => handleChange(2, e)}
								className={`form-control form_custom${showValidation && errors.institution2 ? ' is-invalid' : ''}`}
								placeholder="Institution Name"
								required
							/>
							{showValidation && errors.institution2 && (
								<div className="invalid-feedback d-block">{errors.institution2}</div>
							)}
						</div>
					</Col>

					<Col xl="6">
						<div className="personal_formdetail">
							<h5>&nbsp;</h5>
							<input
								type="text"
								name="institution_webpage"
								value={recommendation2.institution_webpage}
								onChange={(e) => handleChange(2, e)}
								className={`form-control form_cap form_custom${
									showValidation && errors.institution_webpage2 ? ' is-invalid' : ''
								}`}
								placeholder="Institution Webpage"
								required
							/>
							{showValidation && errors.institution_webpage2 && (
								<div className="invalid-feedback d-block">{errors.institution_webpage2}</div>
							)}
							{webpageError2 && (
								<div className="invalid-feedback" style={{ display: 'block' }}>
									{webpageError2}
								</div>
							)}
						</div>
					</Col>
				</Row>

				<Row>
					<Col xl="12">
						<UploadLetter onFileSelect={(file) => handleFileSelect(2, file)} initialFile={recommendation2.file} />
						{showValidation && errors.file2 && <div className="invalid-feedback d-block">{errors.file2}</div>}
					</Col>
				</Row>
			</div>
		</>
	);
});

export default Recommendations;
